/**
 * Typed accessors over the generated JSON (ADR-004). Regenerate with
 * `npm run data:sync`; never edit generated/ by hand.
 */
import itemsJson from './generated/items.json';
import tiersJson from './generated/tiers.json';
import levelsJson from './generated/levels.json';
import adventuresIndexJson from './generated/adventures/index.json';
import assetsJson from './generated/assets.json';
import uiEmojisJson from './generated/ui-emojis.json';
import imagesJson from './generated/images.json';
import bossesJson from './generated/bosses.json';
import questsJson from './generated/quests.json';
import battlepassJson from './generated/battlepass.json';
import dofusJson from './generated/dofus.json';
import successJson from './generated/success.json';
import type { Difficulty, GameItem, ItemDescriptions, ItemType, Lang, League, LevelEntry, Localized, RarityLevel, Tier } from '../types.js';

export { chapterLoaders } from './generated/adventures/loaders.js';

const itemTextLoaders: Record<Lang, () => Promise<ItemDescriptions>> = {
  fr: () => import('./generated/item-text/item-text-fr.json').then((m) => m.default as unknown as ItemDescriptions),
  en: () => import('./generated/item-text/item-text-en.json').then((m) => m.default as unknown as ItemDescriptions),
};
/**
 * Memoizes an async load per key; a rejected load is forgotten so the next
 * call retries (a flaky network must not break a chunk until reload).
 */
export function cachedLoader<K, V>(load: (key: K) => Promise<V>): (key: K) => Promise<V> {
  const cache = new Map<K, Promise<V>>();
  return (key) => {
    let p = cache.get(key);
    if (!p) {
      p = load(key);
      cache.set(key, p);
      p.catch(() => cache.delete(key));
    }
    return p;
  };
}

/** Item descriptions in one language, fetched once (a separate chunk: only item sheets show them). */
export const loadItemDescriptions = cachedLoader((lang: Lang) => itemTextLoaders[lang]());

export const items = itemsJson as unknown as Record<ItemType, GameItem[]>;
export const tiers = tiersJson.tiers as Tier[];
export const rarityLevels = tiersJson.rarityLevels as RarityLevel[];
/** Collection bonus % per level, by tier (`data/items.ts#effectRates`). */
export const effectRates = tiersJson.effectRates as number[];
/** Player levels at which team slots unlock (`data/items.ts#unlockTeamSlots`). */
export const unlockTeamSlots = tiersJson.unlockTeamSlots as number[];
export const levelTable = levelsJson.level as LevelEntry[];
export const leagues = levelsJson.leagues as League[];

export interface ChapterMeta {
  act: number;
  chapter: number;
  name: string;
  emoji: string;
  /** Boss/chapter artwork URL (Discord attachment; see docs/03-assets.md). */
  image: string;
  element: string;
  steps: number;
}
export const chapterIndex = adventuresIndexJson.chapters as ChapterMeta[];
export const difficulties = adventuresIndexJson.difficulties as Difficulty[];
export const acts = adventuresIndexJson.acts as { id: number; name: string }[];

/** Discord emoji id → { name, animated } for every token in the data. */
export const emojiManifest = assetsJson as Record<string, { name: string; animated: boolean }>;
/** Every artwork URL in the data (portraits, chapter art, speaker avatars). */
export const artworkUrls = imagesJson as string[];
/** World boss pool (`data/boss-list.ts#dofusBosses`): name + emoji token. */
export const bosses = bossesJson as { name: string; emoji: string }[];
export interface QuestDef {
  id: number;
  difficulty: number;
  /** Objective kind (`value.type` in the bot), e.g. `boss`, `adventures-hard`. */
  type: string;
  data: number;
  premium: boolean;
  /** Template with `%VAR%` placeholders. */
  name: Localized;
}
/** Quest pool (`data/quest-list.ts`), ids identical in every language. */
export const questDefs = questsJson as { daily: QuestDef[]; weekly: QuestDef[] };

export interface BattlepassItem {
  id: number;
  type: string;
  value: string;
  amount: number;
  highlight: boolean;
}
/** Battlepass free and premium tracks, tier costs and the repeating bonus step (`rpg/battlepass/data.ts`). */
export const battlepassData = battlepassJson as {
  free: BattlepassItem[];
  premium: BattlepassItem[];
  cost: number[];
  bonus: BattlepassItem & { bonus: boolean };
};
export interface DofusTier {
  key: 'one' | 'two' | 'three' | 'four' | 'five' | 'six';
  emoji: string;
  name: string;
  /** Symbols shown on a missed reel (`buildFailLine`); empty for tiers that never miss. */
  no: string[];
}
/** Dofus roulette tiers of the Dofus universe (`data/universe.ts`), tier one → six. */
export const dofusTiers = dofusJson as DofusTier[];
export interface SuccessDef {
  /** 1-based position in the bot's list (the `rpg_success` document id). */
  id: number;
  group: string;
  /** Index inside its group (locale lookup). */
  key: number;
  value: number | string | { type: string | number; data?: string | number | null };
  rewards: { fake: number; loot: number; shop: number; leg: number; title: string; ornement: string | number; points: number };
  /** Bot text (emoji tokens, **bold**, `%VAR%` for the Dofus symbol). */
  name: Localized;
}
/** Achievements (`data/success-list.ts`). */
export const successDefs = successJson as SuccessDef[];
/** Bot UI emoji constants by export name (`utils/emojis.ts`). */
export const uiEmojis = uiEmojisJson as Record<string, string>;

const byId: Record<ItemType, Map<number, GameItem>> = {
  characters: new Map(items.characters.map((i) => [i.id, i])),
  weapons: new Map(items.weapons.map((i) => [i.id, i])),
  shields: new Map(items.shields.map((i) => [i.id, i])),
  pets: new Map(items.pets.map((i) => [i.id, i])),
};

export function getItem(type: ItemType, id: number): GameItem | undefined {
  return byId[type].get(id);
}

export function getTier(id: number): Tier | undefined {
  return tiers.find((t) => t.id === id);
}
